import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { EmvioLoadingSpinner as LoadingSpinner } from '@nexio/emvio-util-ui';
import './MerchantDetailsHeader.scss';
import CopyToClipboardButton from '../../../components/CopyToClipboardButton';
import config from '../../../config/config';
import { toast } from 'react-toastify';
import ErrorCard from '../../ErrorCard/ErrorCard';

export const BOARDING_STATUSES = [
    'Pre-Vet',
    'Application Created',
    'Application Sent',
    'Application Received',
    'Underwriting',
    'Pending',
    'Underwritten',
    'Declined',
    'Boarded',
    'Live',
    'Inactive',
    'Closed'
];

export const STATUS_MAP = {
    'showPrevetId': ['prevet'],
    'showAppId': ['Application Created', 'Application Sent', 'Application Received', 'Underwriting', 'Underwritten', 'Pending', 'Declined'],
    'showAppLink': ['Application Created', 'Application Sent'],
    'showApp': ['Application Created', 'Application Received'],
    'showCmcMerchantId': ['Boarded', 'Live', 'Inactive', 'Closed', 'Underwritten', 'Inactive', 'Closed'],
    'showMid': ['Boarded', 'Live', 'Inactive', 'Closed', 'Underwritten', 'Inactive', 'Closed']
};

const STATUS_BORDER_COLORS = {
    'prevet': '#005486',
    'Application Created': '#CD8E42',
    'Application Sent': '#117FD3',
    'Application Received': '#008000',
    'Underwriting': '#714CA1',
    'Pending': '#EE8D12',
    'Underwritten': '#6537EC',
    'Declined': '#CF3C2B',
    'Boarded': '#0099BE',
    'Live': '#109E10',
    'Inactive': '#808080',
    'Closed': '#E26500'
};

// use this map to determine which table/tab to sort by when clicking on a status card in Path To Live page
export const STATUS_TO_TABLE_SORT_MAP = {
    'Pre-Vet': 'prevet',
    'Application Created': 'applications',
    'Application Sent': 'applications',
    'Application Received': 'applications',
    'Underwriting': 'applications',
    'Pending': 'applications',
    'Underwritten': 'applications',
    'Declined': 'applications',
    'Boarded': 'merchants',
    'Live': 'merchants',
    'Inactive': 'merchants',
    'Closed': 'merchants'
};

/**
 * CopyToClipboardRow
 * Renders a label and value with a copy-to-clipboard icon.
 * Can optionally render the value as an anchor hyperlink.
 * Uses the reusable CopyToClipboardButton component.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Field label text
 * @param {string} props.value - Value to display and copy
 * @param {boolean} [props.isLink] - Whether to render the value as a hyperlink
 * @param {string} [props.className] - Extra class names for the wrapper div
 * @param {function} [props.onCopy] - Optional callback function called after successful copy
 * @param {function} [props.onError] - Optional callback function called if copy fails
 * @returns {JSX.Element | null} React element for the field
 */
const CopyToClipboardRow = ({ label, value, onCopy, onError, isLink = false, className = '' }) => {
    // Ref to access CopyToClipboardButton's handleCopy method
    const copyButtonRef = React.useRef(null);

    if (!value) {
        return null;
    }

    /**
     * Handles click on the value span - triggers the copy button's copy function
     */
    const handleValueClick = () => {
        if (copyButtonRef.current) {
            copyButtonRef.current.handleCopy();
        }
    };

    const wrapperClassName = ['merchant-details-id', className]
        .filter((name) => name.length > 0)
        .join(' ');

    return (
        <div className={wrapperClassName}>
            {label}:{' '}
            {isLink ? (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    {value}
                </a>
            ) : (
                <span
                    className="merchant-details-value"
                    onClick={handleValueClick}
                >
                    {value}
                </span>
            )}
            <CopyToClipboardButton
                ref={copyButtonRef}
                text={value}
                onCopy={onCopy}
                onError={onError}
            />
        </div>
    );
};

export const MerchantDetailsHeader = ({ merchantDetails }) => {
    const status = merchantDetails?.status;
    const merchantName = merchantDetails?.legalName;
    const contactEmail = merchantDetails?.contactEmail;
    const contactName = merchantDetails?.contactName;
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (merchantDetails && !loaded) {
            setLoaded(true);
        }
    }, [merchantDetails, loaded]);

    if (!loaded) return <LoadingSpinner />;

    if (!merchantDetails) return <ErrorCard
        errorMessage={
            'The selected Application record was not found. Go back and look for a newer version of the Application.'
        }
        errorLinkMessage={'Go back.'}
    />;

    /**
     * Handles copy errors by showing a toast notification.
     * The CopyToClipboardButton component handles successful copies internally.
     */
    const handleCopyError = (error) => {
        console.error('Failed to copy to clipboard:', error);
        toast.error('Failed to copy to clipboard');
    };

    const renderStatusId = (status) => {
        let idName = '';
        let idValue = '';
        if (status === 'prevet') {
            idName = 'Pre-Vet ID';
            idValue = _.get(merchantDetails, 'prevetId', '');
        } else if (STATUS_MAP['showAppId'].includes(status)) {
            idName = 'App ID';
            idValue = _.get(merchantDetails, 'onlineApplicationId', '');
        } else if (STATUS_MAP['showMid'].includes(status)) {
            idName = 'Merchant ID';
            idValue = _.get(merchantDetails, 'MID', '') || _.get(merchantDetails, 'cmcMerchantId', '');
        }

        return (
            <div className="merchant-details-id">{idName}: {idValue}</div>
        );
    };

    const renderStatusBadge = (status) => {
        let statusBadgeText = '';
        if (status === 'prevet') {
            statusBadgeText = 'Pre-Vet';
        } else if (STATUS_MAP['showAppId'].includes(status)) {
            statusBadgeText = status;
        } else if (STATUS_MAP['showMid'].includes(status)) {
            statusBadgeText = status;
        }

        return (
            <div className="merchant-details-badge" style={{ backgroundColor: STATUS_BORDER_COLORS[status] }}>
                {statusBadgeText}
            </div>
        );
    };

    const onlineApplicationUrl = `${config.onlineAppMerchantBaseUrl}${_.get(merchantDetails, 'applicationLink', '')}`;
    const onlineApplicationToken = _.get(merchantDetails, 'applicationToken', '');

    return (
        <div className="merchant-details-header">
            <div className="merchant-details-info">
                <h2 className="merchant-details-name">{merchantName || 'Name Not Available'}</h2>
                {renderStatusId(status)}
                {!_.isNull(contactName) && <div className="merchant-details-id">Contact: {contactName}</div>}
                {!_.isNull(contactEmail) && <div className="merchant-details-id">Contact Email: {contactEmail}</div>}
                {STATUS_MAP['showAppLink'].includes(status) && (
                    <>
                        <CopyToClipboardRow
                            label="App Link"
                            value={onlineApplicationUrl}
                            isLink
                            className="merchant-details-app-link"
                            onError={handleCopyError}
                        />
                        <CopyToClipboardRow
                            label="Token"
                            value={onlineApplicationToken}
                            className="merchant-details-app-token"
                            onError={handleCopyError}
                        />
                    </>

                )}
            </div>
            {renderStatusBadge(status)}
        </div>
    );
};

export default MerchantDetailsHeader;

